import { DayOfWeek, LocalDate, Month } from "@js-joda/core";
import {
  CashflowItem,
  business_days,
  every_x_days,
  every_x_months,
  every_x_months_last_day,
  every_x_months_last_working_day,
  every_x_weeks,
  every_x_years,
  IncomesItem,
  LedgerEntry_Gross,
  LedgerEntry_Net,
  MonthlyRecurrenceRule,
  OutgoingItem,
  ParentalLeavePolicy,
  PayBreakdownSegment,
  PaySegment,
  RecurrenceRule,
  weekdays,
} from "./types-v2";
import { occurrencesBetween } from "./engine";
import { calculateIndividualUkTaxAndNI } from "../tax";
import { isBetweenInclusive } from "./date-utils";

// UK SMP weekly flat rate by tax year start (April 6).
// Source: gov.uk/maternity-pay-leave/pay
const SMP_WEEKLY_RATES_PENCE: Record<number, number> = {
  2021: 15197, // £151.97/wk 2021/22
  2022: 15666, // £156.66/wk 2022/23
  2023: 17248, // £172.48/wk 2023/24
  2024: 18403, // £184.03/wk 2024/25
  2025: 18718, // £187.18/wk 2025/26
  2026: 19432, // £194.32/wk 2026/27
};
const SMP_YEARS_DESC = Object.keys(SMP_WEEKLY_RATES_PENCE)
  .map(Number)
  .sort((a, b) => b - a);

// UK statutory tax-free limit for redundancy / termination payments (£30,000).
// Anything above this is taxable. Source: gov.uk/redundancy-your-rights/tax
const REDUNDANCY_TAX_FREE_CAP_PENCE = 30_000 * 100;

// UK statutory redundancy: maximum weekly pay used in the calculation, by tax
// year start (April 6). Source: gov.uk/redundancy-pay
const STATUTORY_WEEKLY_CAP_PENCE: Record<number, number> = {
  2022: 57100, // £571/wk 2022/23
  2023: 64300, // £643/wk 2023/24
  2024: 70000, // £700/wk 2024/25
  2025: 71900, // £719/wk 2025/26
  2026: 75100, // £751/wk 2026/27
};
const STATUTORY_CAP_YEARS_DESC = Object.keys(STATUTORY_WEEKLY_CAP_PENCE)
  .map(Number)
  .sort((a, b) => b - a);

// Statutory weekly pay cap applying on a given date (uses the latest known
// figure for tax years beyond the published values).
export function getStatutoryWeeklyCapPence(date: LocalDate): number {
  const taxYearStart =
    date.monthValue() > 4 || (date.monthValue() === 4 && date.dayOfMonth() >= 6)
      ? date.year()
      : date.year() - 1;

  const match = STATUTORY_CAP_YEARS_DESC.find((y) => y <= taxYearStart);
  return match !== undefined
    ? STATUTORY_WEEKLY_CAP_PENCE[match]
    : STATUTORY_WEEKLY_CAP_PENCE[
        STATUTORY_CAP_YEARS_DESC[STATUTORY_CAP_YEARS_DESC.length - 1]
      ];
}

function getSmpDailyRatePence(date: LocalDate): number {
  // UK tax year starts April 6
  const taxYearStart =
    date.monthValue() > 4 || (date.monthValue() === 4 && date.dayOfMonth() >= 6)
      ? date.year()
      : date.year() - 1;

  const match = SMP_YEARS_DESC.find((y) => y <= taxYearStart);
  const weeklyRatePence =
    match !== undefined
      ? SMP_WEEKLY_RATES_PENCE[match]
      : SMP_WEEKLY_RATES_PENCE[SMP_YEARS_DESC[SMP_YEARS_DESC.length - 1]]; // earliest known

  return weeklyRatePence / 7;
}

export function buildLedger(
  requestStartDate: LocalDate, // generate up to start of TAX year to help calculate tax // show different range on UI
  requestEndDate: LocalDate, // generate up to end of TAX year to help calculate tax // show different range on UI?
  items: CashflowItem[],
  autoTax: boolean = false,
): LedgerEntry_Net[] {
  const grossLedger: LedgerEntry_Gross[] = [];
  const netLedger: LedgerEntry_Net[] = [];

  const requestTaxYearStartDate = getStartOfTaxYear(requestStartDate);
  const requestTaxYearEndDate = getEndOfTaxYear(requestEndDate);

  for (const item of items) {
    if (item.type === "INCOME_ITEM") {
      const { grossEntries, netEntries } = processIncome(
        item,
        requestTaxYearStartDate,
        requestTaxYearEndDate,
      );

      grossEntries.forEach((x) => {
        grossLedger.push(x);
      });

      netEntries.forEach((x) => {
        netLedger.push(x);
      });
    }

    if (item.type === "OUTGOINGS_ITEM") {
      const y = processOutgoing(item, requestStartDate, requestEndDate);

      y.forEach((x) => {
        netLedger.push(x);
      });
    }
  }

  const nettedGrossLedger = convertGrossLedgerToNet(grossLedger).filter((x) =>
    isBetweenInclusive(x.date, {
      dateFromInclusive: requestStartDate,
      dateToInclusive: requestEndDate,
    }),
  );

  const combinedNetLedger = netLedger.concat(nettedGrossLedger);

  return combinedNetLedger.sort((a, b) => a.date.compareTo(b.date));
}

export type BalanceDay = {
  date: LocalDate;
  transactions: LedgerEntry_Net[];
  openingBalancePence: number;
  balanceChangePence: number;
  closingBalancePence: number;
};

export function buildBalanceByDay(
  netLedger: LedgerEntry_Net[],
  requestStartDate: LocalDate,
  requestEndDate: LocalDate,
  initialBalancePence: number,
) {
  const bucketedByDates = bucketEntriesByDate(netLedger);
  const balanceHistory: BalanceDay[] = [];

  for (
    let d = requestStartDate;
    !d.isAfter(requestEndDate);
    d = d.plusDays(1)
  ) {
    const previousDay =
      balanceHistory.length > 0
        ? balanceHistory[balanceHistory.length - 1]
        : null;

    const todayData = bucketedByDates[d.toString()];
    const todaysEntries = todayData ? todayData.entries : [];

    const balanceChangePence = todaysEntries.reduce(
      (accumulator, currentValue) => accumulator + currentValue.amountPence,
      0,
    );

    const openingBalancePence = previousDay
      ? previousDay.closingBalancePence
      : initialBalancePence; //// make this the initial balance

    balanceHistory.push({
      date: d,
      transactions: todaysEntries,
      openingBalancePence,
      balanceChangePence,
      closingBalancePence: openingBalancePence + balanceChangePence,
    });
  }

  return balanceHistory;
}

const mapGrossToNet = (
  { amountPence, ...x }: LedgerEntry_Gross,
  effectiveTaxRate: number,
): LedgerEntry_Net => {
  return {
    ...x,
    amountPence: Math.round(amountPence * (1 - effectiveTaxRate)),
    percentageAlreadyTaxed: effectiveTaxRate,
    payBreakdown: x.payBreakdown,
  };
};

type TaxYear = {
  taxDates: { start: LocalDate; end: LocalDate };
  grossTotalPence: number;
  ledgerEntries: Array<LedgerEntry_Gross>;
};

type EntriesByDay = {
  date: LocalDate;
  entries: LedgerEntry_Net[];
  // openingBalance: number;
  // balanceChange: number;
  // closingBalance: number;
};

export const bucketEntriesByDate = (
  grossLedger: LedgerEntry_Net[],
): Record<string, EntriesByDay> => {
  // convert to Net later, rename this,...
  const entriesByDay: Record<string, EntriesByDay> = {};

  grossLedger.forEach((x) => {
    const date = x.date.toString();

    if (entriesByDay[date]) {
      entriesByDay[date].entries.push(x);
    } else {
      entriesByDay[date] = {
        date: x.date,
        entries: [x],
      };
    }
  });

  return entriesByDay;
};

export const bucketEntriesByUserAndTaxYear = (
  grossLedger: LedgerEntry_Gross[],
): Record<string, Record<string, TaxYear>> => {
  const result: Record<string, Record<string, TaxYear>> = {};

  grossLedger.forEach((x) => {
    const userId = x.userId;
    const taxYearKey = JSON.stringify(x.taxDates);

    if (!result[userId]) {
      result[userId] = {};
    }

    if (!result[userId][taxYearKey]) {
      result[userId][taxYearKey] = {
        taxDates: x.taxDates,
        grossTotalPence: 0,
        ledgerEntries: [],
      };
    }

    result[userId][taxYearKey].grossTotalPence += x.amountPence;
    result[userId][taxYearKey].ledgerEntries.push(x);
  });

  return result;
};

export const convertGrossLedgerToNet = (
  grossLedger: LedgerEntry_Gross[],
  // usersById: Record<string, User>,
): LedgerEntry_Net[] => {
  const nettedItems: LedgerEntry_Net[] = [];

  const bucketed = bucketEntriesByUserAndTaxYear(grossLedger);

  for (const userId in bucketed) {
    const userBuckets = bucketed[userId];

    for (const taxYearKey in userBuckets) {
      const item = userBuckets[taxYearKey];

      const result = calculateIndividualUkTaxAndNI({
        userId,
        grossPounds: penceToPounds(item.grossTotalPence),
        taxYear: item.taxDates,
      });

      item.ledgerEntries.forEach((x) => {
        const y = mapGrossToNet(x, result.effectiveRate);

        nettedItems.push(y);
      });
    }
  }

  return nettedItems;
};

export function getEndOfTaxYear(date: LocalDate): LocalDate {
  const year = date.year();
  const taxYearEnd = LocalDate.of(year, Month.APRIL, 5);

  // If the date is after 5 April, the end of the tax year is next year's 5 April
  return date.isAfter(taxYearEnd)
    ? LocalDate.of(year + 1, Month.APRIL, 5)
    : taxYearEnd;
}

export function getStartOfTaxYear(date: LocalDate): LocalDate {
  const year = date.year();
  const taxYearStart = LocalDate.of(year, Month.APRIL, 6);

  // If the date is before 6 April, the end of the tax year is next year's 5 April
  return date.isBefore(taxYearStart)
    ? LocalDate.of(year - 1, Month.APRIL, 6)
    : taxYearStart;
}

function processIncome(
  income: IncomesItem,
  from: LocalDate,
  to: LocalDate,
): { grossEntries: LedgerEntry_Gross[]; netEntries: LedgerEntry_Net[] } {
  const effectiveFrom = income.dateFilters?.dateFromInclusive
    ? income.dateFilters.dateFromInclusive.isAfter(from)
      ? income.dateFilters.dateFromInclusive
      : from
    : from;

  let effectiveTo = income.dateFilters?.dateToInclusive
    ? income.dateFilters.dateToInclusive.isBefore(to)
      ? income.dateFilters.dateToInclusive
      : to
    : to;

  // Redundancy stops the salary after its end date, regardless of dateFilters.
  if (income.redundancy && income.redundancy.endDate.isBefore(effectiveTo)) {
    effectiveTo = income.redundancy.endDate;
  }

  if (effectiveFrom.isAfter(effectiveTo)) {
    return { grossEntries: [], netEntries: [] };
  }

  const anchorStart =
    income.recurrence.kind === every_x_weeks
      ? (income.dateFilters?.dateFromInclusive ?? effectiveFrom)
      : undefined;

  const dates = occurrencesBetween(
    income.recurrence,
    effectiveFrom,
    effectiveTo,
    anchorStart,
  );

  const grossEntries: LedgerEntry_Gross[] = [];
  const netEntries: LedgerEntry_Net[] = [];

  let previousPayDate: LocalDate | null = null;

  for (const payDate of dates) {
    const { amountPence: amountDuePence, payBreakdown } = amountDueOnPayDate(
      income.recurrence,
      payDate,
      previousPayDate,
      income.amountPence,
      income.dateFilters?.dateFromInclusive ?? LocalDate.EPOCH_0,
      income.parentalLeavePolicy,
    );

    if (income.taxable) {
      grossEntries.push({
        date: payDate,
        itemId: income.id,
        userId: income.userId,
        name: income.description,
        taxDates: {
          start: getStartOfTaxYear(payDate),
          end: getEndOfTaxYear(payDate),
        },
        amountPence: amountDuePence,
        payBreakdown,
      });
    } else {
      netEntries.push({
        date: payDate,
        itemId: income.id,
        name: income.description,
        taxDates: {
          start: getStartOfTaxYear(payDate),
          end: getEndOfTaxYear(payDate),
        },
        amountPence: amountDuePence,
        percentageAlreadyTaxed: 0,
        payBreakdown,
      });
    }

    previousPayDate = payDate;
  }

  // Bonuses: taxable one-off payments, either a percentage of the salary or a
  // fixed amount, on their due date.
  if (income.bonuses) {
    for (const b of income.bonuses) {
      if (b.date.isBefore(from) || b.date.isAfter(to)) continue;

      const bonusPence =
        b.kind === "fixed"
          ? (b.amountPence ?? 0)
          : Math.round(income.amountPence * (b.percentage ?? 0));

      if (bonusPence <= 0) continue;

      grossEntries.push({
        date: b.date,
        itemId: income.id,
        userId: income.userId,
        name: `${income.description} (bonus)`,
        taxDates: {
          start: getStartOfTaxYear(b.date),
          end: getEndOfTaxYear(b.date),
        },
        amountPence: bonusPence,
      });
    }
  }

  // Redundancy settlement is paid on `paidOn` (the final payroll run), which
  // may differ from the last day of employment; it defaults to the end date.
  if (income.redundancy) {
    const { endDate, weeks, payBasis, noticeMonths, holidayDays } =
      income.redundancy;
    const payDate = income.redundancy.paidOn ?? endDate;
    const inWindow = !payDate.isBefore(from) && !payDate.isAfter(to);

    if (inWindow) {
      // Taxed in the tax year the money is received (the pay date).
      const taxDates = {
        start: getStartOfTaxYear(payDate),
        end: getEndOfTaxYear(payDate),
      };

      // Redundancy payment = number of weeks × weekly pay. On a statutory basis
      // the weekly pay is capped at the government's weekly limit.
      const fullWeeklyPence = income.amountPence / 52;
      const weeklyPence =
        payBasis === "statutory"
          ? Math.min(fullWeeklyPence, getStatutoryWeeklyCapPence(endDate))
          : fullWeeklyPence;
      const redundancyPaymentPence = Math.round(weeklyPence * weeks);

      // Only the first £30,000 of the redundancy payment is tax-free; the
      // excess is taxable and pooled into the tax year at the marginal rate.
      const taxFreePortionPence = Math.min(
        redundancyPaymentPence,
        REDUNDANCY_TAX_FREE_CAP_PENCE,
      );
      const taxableExcessPence = Math.max(
        0,
        redundancyPaymentPence - REDUNDANCY_TAX_FREE_CAP_PENCE,
      );

      if (taxFreePortionPence > 0) {
        netEntries.push({
          date: payDate,
          itemId: income.id,
          name: `${income.description} (redundancy — tax free)`,
          taxDates,
          amountPence: taxFreePortionPence,
          percentageAlreadyTaxed: 0,
        });
      }

      if (taxableExcessPence > 0) {
        grossEntries.push({
          date: payDate,
          itemId: income.id,
          userId: income.userId,
          name: `${income.description} (redundancy — taxable over £30k)`,
          taxDates,
          amountPence: taxableExcessPence,
        });
      }

      // Payment in lieu of notice: taxable, worth `noticeMonths` of monthly salary.
      if (noticeMonths && noticeMonths > 0) {
        const monthlyPence = Math.round(income.amountPence / 12);
        grossEntries.push({
          date: payDate,
          itemId: income.id,
          userId: income.userId,
          name: `${income.description} (pay in lieu of notice)`,
          taxDates,
          amountPence: monthlyPence * noticeMonths,
        });
      }

      // Outstanding holiday: normal (fully taxable) pay, at the full daily rate
      // (annual salary over 260 working days), regardless of the pay basis.
      if (holidayDays && holidayDays > 0) {
        const dailyPence = income.amountPence / 260;
        grossEntries.push({
          date: payDate,
          itemId: income.id,
          userId: income.userId,
          name: `${income.description} (outstanding holiday)`,
          taxDates,
          amountPence: Math.round(dailyPence * holidayDays),
        });
      }
    }
  }

  // A monthly salary pays at (or near) month end, so if employment ends
  // mid-cycle — whether via a redundancy or just an "Until" date — the days
  // since the last payday are otherwise unpaid. Accrue that final part-month at
  // the daily rate and pay it out (on the settlement date if a redundancy sets
  // one, otherwise on the last day of employment).
  const isMonthly =
    income.recurrence.kind === every_x_months ||
    income.recurrence.kind === every_x_months_last_working_day ||
    income.recurrence.kind === every_x_months_last_day;

  const filterEnd = income.dateFilters?.dateToInclusive;
  const redundancyEnd = income.redundancy?.endDate;
  const salaryEndDate =
    filterEnd && redundancyEnd
      ? filterEnd.isBefore(redundancyEnd)
        ? filterEnd
        : redundancyEnd
      : (filterEnd ?? redundancyEnd ?? null);

  if (
    isMonthly &&
    salaryEndDate &&
    !salaryEndDate.isBefore(from) &&
    !salaryEndDate.isAfter(to)
  ) {
    const stubPayDate = income.redundancy?.paidOn ?? salaryEndDate;
    const lastPayDate = dates.length ? dates[dates.length - 1] : null;
    const stubFrom = lastPayDate ? lastPayDate.plusDays(1) : effectiveFrom;

    if (!stubFrom.isAfter(salaryEndDate)) {
      const stubPence = salaryForPeriod(stubFrom, salaryEndDate, (d) =>
        dailyRate(d, income.amountPence),
      );

      if (stubPence > 0) {
        const stubTaxDates = {
          start: getStartOfTaxYear(stubPayDate),
          end: getEndOfTaxYear(stubPayDate),
        };

        if (income.taxable) {
          grossEntries.push({
            date: stubPayDate,
            itemId: income.id,
            userId: income.userId,
            name: `${income.description} (final salary)`,
            taxDates: stubTaxDates,
            amountPence: stubPence,
          });
        } else {
          netEntries.push({
            date: stubPayDate,
            itemId: income.id,
            name: `${income.description} (final salary)`,
            taxDates: stubTaxDates,
            amountPence: stubPence,
            percentageAlreadyTaxed: 0,
          });
        }
      }
    }
  }

  return { grossEntries, netEntries };
}

function processOutgoing(
  outgoing: OutgoingItem,
  from: LocalDate,
  to: LocalDate,
): LedgerEntry_Net[] {
  const effectiveFrom = outgoing.dateFilters?.dateFromInclusive
    ? outgoing.dateFilters.dateFromInclusive.isAfter(from)
      ? outgoing.dateFilters.dateFromInclusive
      : from
    : from;
  const effectiveTo = outgoing.dateFilters?.dateToInclusive
    ? outgoing.dateFilters.dateToInclusive.isBefore(to)
      ? outgoing.dateFilters.dateToInclusive
      : to
    : to;

  if (effectiveFrom.isAfter(effectiveTo)) {
    return [];
  }

  const anchorStart =
    outgoing.recurrence.kind === every_x_weeks
      ? (outgoing.dateFilters?.dateFromInclusive ?? effectiveFrom)
      : undefined;

  const dates = occurrencesBetween(
    outgoing.recurrence,
    effectiveFrom,
    effectiveTo,
    anchorStart,
  );

  const res = dates.map((date) => ({
    date,
    itemId: outgoing.id,
    name: outgoing.description,
    taxDates: {
      start: getStartOfTaxYear(date),
      end: getEndOfTaxYear(date),
    },
    amountPence: -outgoing.amountPence,
    percentageAlreadyTaxed: 0,
  }));

  return res;
}

export function amountDueOnPayDate(
  payRule: RecurrenceRule,
  payDate: LocalDate,
  previousPayDate: LocalDate | null,
  grossSalaryPence: number,
  itemStartDate: LocalDate,
  policy?: ParentalLeavePolicy,
): { amountPence: number; payBreakdown?: PayBreakdownSegment[] } {
  // Determine pay period
  const period = resolvePayPeriod(
    payDate,
    payRule,
    itemStartDate,
    previousPayDate,
  );

  if (
    payRule.kind === "specific-dates" ||
    payRule.kind === every_x_years ||
    payRule.kind === every_x_weeks ||
    payRule.kind === every_x_days ||
    payRule.kind === weekdays ||
    payRule.kind === business_days
  ) {
    return { amountPence: grossSalaryPence };
  }

  // Build segments if policy exists
  const segments = policy ? buildPaySegments(policy) : [];

  if (!segments.length) {
    return {
      amountPence: salaryForPeriod(period.from, period.to, (d) =>
        dailyRate(d, grossSalaryPence),
      ),
    };
  }

  // Accumulate chronological runs of consecutive same-label days
  const runs: PayBreakdownSegment[] = [];

  const segLabel = (seg: PaySegment | undefined): string => {
    if (!seg) return "full pay";
    if ("smp" in seg) return "SMP";
    if (seg.multiplier === 1) return "full pay";
    return `${Math.round(seg.multiplier * 100)}%`;
  };

  const amountPence = salaryForPeriod(period.from, period.to, (d) => {
    const seg = segments.find((s) => !d.isBefore(s.from) && !d.isAfter(s.to));
    const label = segLabel(seg);

    const last = runs[runs.length - 1];
    if (last && last.label === label) {
      last.days++;
    } else {
      runs.push({ days: 1, label });
    }

    if (seg && "smp" in seg) return getSmpDailyRatePence(d);
    if (seg) return dailyRate(d, grossSalaryPence) * seg.multiplier;
    return dailyRate(d, grossSalaryPence);
  });

  // Merge non-consecutive runs with the same label into one entry
  const merged: PayBreakdownSegment[] = [];
  const seen = new Map<string, PayBreakdownSegment>();
  for (const run of runs) {
    const existing = seen.get(run.label);
    if (existing) {
      existing.days += run.days;
    } else {
      const entry = { ...run };
      merged.push(entry);
      seen.set(run.label, entry);
    }
  }

  // Order: preserve first-occurrence order (chronological) — Map insertion order is stable
  const payBreakdown = merged;

  // Only return breakdown if some days were reduced (i.e. not all full pay)
  const allFullPay =
    payBreakdown.length === 1 && payBreakdown[0].label === "full pay";
  return { amountPence, payBreakdown: allFullPay ? undefined : payBreakdown };
}

export function resolvePayPeriod(
  payDate: LocalDate,
  rule: RecurrenceRule,
  itemStartDate: LocalDate,
  previousPayDate: LocalDate | null,
): { from: LocalDate; to: LocalDate } {
  // Monthly salary = calendar month
  if (rule.kind === "every-x-months") {
    let from = LocalDate.of(payDate.year(), payDate.monthValue(), 1); // 1st of month
    let to = LocalDate.of(
      payDate.year(),
      payDate.monthValue(),
      payDate.lengthOfMonth(),
    ); // last day of month

    // Ensure we never pay before the item starts
    if (from.isBefore(itemStartDate)) from = itemStartDate;

    return { from, to };
  }

  // Fallback: period = previous pay date → pay date
  return {
    from: (() => {
      if (previousPayDate) return previousPayDate.plusDays(1);

      const startOfMonth = LocalDate.of(
        payDate.year(),
        payDate.monthValue(),
        1,
      );
      return startOfMonth.isBefore(itemStartDate)
        ? itemStartDate
        : startOfMonth;
    })(),
    to: payDate,
  };
}

function dailyRate(date: LocalDate, grossSalaryPence: number): number {
  const daysInMonth = date.lengthOfMonth();
  return grossSalaryPence / 12 / daysInMonth;
}

// SMP is only payable for a maximum of 39 weeks from leave start (UK statutory limit)
const SMP_MAX_DAYS = 39 * 7;

function buildPaySegments(policy: ParentalLeavePolicy): PaySegment[] {
  const segments: PaySegment[] = [];
  let cursor = policy.startsFrom;
  const smpCutoff = policy.startsFrom.plusDays(SMP_MAX_DAYS);
  let lastSegmentArgs: { multiplier: number } | { smp: true } = {
    multiplier: 1,
  };

  for (const rule of policy.sequentialRules) {
    const endExclusive = cursor.plus(
      rule.amountOfChronoUnits,
      rule.chronoUnits,
    );
    const segmentArgs =
      rule.amountType === "smp"
        ? ({ smp: true } as const)
        : { multiplier: rule.percentage };

    if (rule.amountType === "smp" && cursor.isBefore(smpCutoff)) {
      // SMP only up to the 39-week cutoff; remainder of this rule period is unpaid (£0)
      const smpEnd = endExclusive.isAfter(smpCutoff) ? smpCutoff : endExclusive;
      segments.push({ from: cursor, to: smpEnd.minusDays(1), smp: true });
      if (endExclusive.isAfter(smpCutoff)) {
        segments.push({
          from: smpCutoff,
          to: endExclusive.minusDays(1),
          multiplier: 0,
        });
      }
    } else if (rule.amountType === "smp") {
      // Entirely beyond 39 weeks — unpaid
      segments.push({
        from: cursor,
        to: endExclusive.minusDays(1),
        multiplier: 0,
      });
    } else {
      segments.push({
        from: cursor,
        to: endExclusive.minusDays(1),
        ...segmentArgs,
      });
    }

    lastSegmentArgs = segmentArgs;
    cursor = endExclusive;
  }

  const farFuture = LocalDate.of(9999, 12, 31);
  if (policy.afterPolicy === "leave-job") {
    segments.push({ from: cursor, to: farFuture, multiplier: 0 });
  } else if (policy.afterPolicy === "last-rate") {
    segments.push({ from: cursor, to: farFuture, ...lastSegmentArgs });
  }

  return segments;
}

function salaryForPeriod(
  periodStart: LocalDate,
  periodEnd: LocalDate,
  dailyRateFor: (d: LocalDate) => number,
): number {
  let total = 0;
  for (let d = periodStart; !d.isAfter(periodEnd); d = d.plusDays(1)) {
    const dailyRate = dailyRateFor(d);
    total += dailyRate;
  }

  return Math.round(total);
}

function penceToPounds(valuePence: number): number {
  return valuePence / 100;
}

export function lastDayOfMonth(year: number, month: number): LocalDate {
  return LocalDate.of(year, month, 1).withDayOfMonth(
    LocalDate.of(year, month, 1).lengthOfMonth(),
  );
}

export function lastWorkingDayOfMonth(year: number, month: number): LocalDate {
  let d = lastDayOfMonth(year, month);

  while (isWeekend(d)) {
    d = d.minusDays(1);
  }

  return d;
}

function isWeekend(d: LocalDate): boolean {
  const dow = d.dayOfWeek();
  return dow === DayOfWeek.SATURDAY || dow === DayOfWeek.SUNDAY;
}

export function alignPayDate(
  base: LocalDate,
  rule: MonthlyRecurrenceRule,
): LocalDate {
  switch (
    rule.align // change to alignment?
  ) {
    case "fixed-day": {
      const day = (rule as any).dayOfMonth ?? base.dayOfMonth();
      const res = base.withDayOfMonth(Math.min(day, base.lengthOfMonth()));
      return res;
    }

    case "last-working-day": {
      const res = lastWorkingDayOfMonth(base.year(), base.monthValue());
      return res;
    }

    case "last-day": {
      const res = lastDayOfMonth(base.year(), base.monthValue());
      return res;
    }

    default:
      throw new Error("Unsupported");
  }
}
